import React from "react";
import { typography, borders, textColors, surfaces, interactive } from "../../../design-system/tokens";
import { getFontFamilyPreviewStack } from "../../../core/utils/fontFamilyUtils";

const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

const WEIGHT_NAMES = {
  100: "Thin",
  200: "Extra Light",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "Semi Bold",
  700: "Bold",
  800: "Extra Bold",
  900: "Black",
};

function WeightChip({ value, selected, onSelect, fontFamilyStack }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`border rounded-lg p-2 text-center transition-all w-full ${
        selected
          ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
          : `${borders.default} ${surfaces.tertiary} ${textColors.primary} ${surfaces.hover}`
      }`}
      aria-pressed={selected}
      title={`${value} — ${WEIGHT_NAMES[value] || ""}`}
    >
      {/* Preview letter */}
      <div
        className="text-2xl leading-none mx-auto mb-1"
        style={{
          fontWeight: value,
          fontFamily: fontFamilyStack,
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Aa
      </div>
      {/* Weight number + name */}
      <div className={`text-[10px] leading-tight font-semibold ${selected ? "text-white/70" : textColors.muted}`}>
        {value}
      </div>
      <div className={`text-[9px] leading-tight ${selected ? "text-white/60" : textColors.muted}`}>
        {WEIGHT_NAMES[value] || ""}
      </div>
    </button>
  );
}

function getPreviewTextFromValues(allValues) {
  if (!allValues || typeof allValues !== "object") return null;
  const candidateFields = [
    "text", "title", "label", "button_text", "subtitle", "content", "description", "name",
  ];
  for (const key of candidateFields) {
    const v = allValues[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
    if (v && typeof v === "object") {
      if (typeof v.content === "string" && v.content.trim().length > 0) return v.content.trim();
      if (typeof v.rendered_content === "string" && v.rendered_content.trim().length > 0) {
        const stripped = v.rendered_content.replace(/<[^>]*>/g, "").trim();
        if (stripped) return stripped;
      }
    }
  }
  return null;
}

function getFontStackFromValues(allValues) {
  if (!allValues || typeof allValues !== "object") return null;
  const v = allValues.character_style?.font_family || allValues.characterStyle?.font_family || allValues.font_family;
  if (!v) return null;
  return getFontFamilyPreviewStack(v);
}

export default function FontWeightField({ label, help, value, onChange, allValues, selectedElement }) {
  const fallback = "The quick brown fox";
  const textRaw =
    getPreviewTextFromValues(selectedElement) ||
    getPreviewTextFromValues(allValues) ||
    fallback;
  const previewText = textRaw.length > 40 ? textRaw.slice(0, 37) + "…" : textRaw;
  const fontFamilyStack = getFontStackFromValues(allValues) || undefined;

  return (
    <div className="field-wrapper">
      <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>{label}</label>

      {/* Weight cards grid */}
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(68px, 1fr))" }}
      >
        {WEIGHTS.map((w) => (
          <WeightChip
            key={w}
            value={w}
            selected={Number(value) === w}
            onSelect={(val) => onChange(val)}
            fontFamilyStack={fontFamilyStack}
          />
        ))}
      </div>

      {/* Live preview strip */}
      <div className={`mt-3 p-3 rounded-lg border ${borders.default} ${surfaces.secondary}`}>
        <div className={`mb-1 ${typography.caption} ${textColors.muted}`}>Preview</div>
        <div
          className={`${textColors.primary} text-base truncate`}
          style={{ fontWeight: Number(value) || 400, fontFamily: fontFamilyStack }}
        >
          {previewText}
        </div>
      </div>

      {help && <div className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
}
